import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
    Plus,
    Search,
    Filter,
    Wrench,
    MoreVertical,
    Calendar
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { workOrderApi, WorkOrder } from '@/lib/api';
import AddMaintenanceDialog from '@/components/maintenance/AddMaintenanceDialog';
import { cn } from '@/lib/utils';
import { format } from 'date-fns';

export default function MaintenancePage() {
    const [searchQuery, setSearchQuery] = useState('');
    const [statusFilter, setStatusFilter] = useState<string>('all');
    const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);

    const { data: workOrders = [], isLoading } = useQuery({
        queryKey: ['workOrders', statusFilter],
        queryFn: async () => {
            // In real app, pass filter to API
            const res = await workOrderApi.getAll(statusFilter === 'all' ? undefined : statusFilter);
            return res.data;
        }
    });

    const filteredOrders = workOrders.filter(order =>
        order.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        order.description.toLowerCase().includes(searchQuery.toLowerCase())
    );

    return (
        <div className="space-y-6">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                <div>
                    <h1 className="text-2xl font-bold text-gray-900">Maintenance</h1>
                    <p className="mt-1 text-sm text-gray-500">
                        Track and manage property maintenance requests
                    </p>
                </div>
                <div className="flex gap-3">
                    <Button onClick={() => setIsAddDialogOpen(true)} className="gap-2">
                        <Plus className="h-4 w-4" />
                        New Request
                    </Button>
                </div>
            </div>

            {/* Filters */}
            <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
                <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                    <input
                        type="text"
                        placeholder="Search maintenance requests..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="h-10 w-full rounded-lg border border-gray-300 bg-white pl-10 pr-4 text-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/20"
                    />
                </div>
                <div className="flex items-center gap-2">
                    <Filter className="h-4 w-4 text-gray-500" />
                    <select
                        className="h-10 rounded-lg border border-gray-300 bg-white px-3 text-sm focus:border-primary-500 focus:outline-none"
                        value={statusFilter}
                        onChange={(e) => setStatusFilter(e.target.value)}
                    >
                        <option value="all">All Status</option>
                        <option value="Open">Open</option>
                        <option value="Scheduled">Scheduled</option>
                        <option value="In Progress">In Progress</option>
                        <option value="Completed">Completed</option>
                    </select>
                </div>
            </div>

            {/* List */}
            <div className="grid gap-4">
                {isLoading ? (
                    <div className="text-center py-10 text-gray-500">Loading requests...</div>
                ) : filteredOrders.length === 0 ? (
                    <div className="flex flex-col items-center justify-center rounded-xl border-2 border-dashed border-gray-200 bg-gray-50 py-12 text-center">
                        <Wrench className="h-12 w-12 text-gray-400" />
                        <h3 className="mt-2 text-sm font-semibold text-gray-900">No maintenance requests</h3>
                        <p className="mt-1 text-sm text-gray-500">Get started by creating a new request.</p>
                        <Button variant="outline" className="mt-4" onClick={() => setIsAddDialogOpen(true)}>
                            Create Request
                        </Button>
                    </div>
                ) : (
                    filteredOrders.map(order => (
                        <MaintenanceCard key={order.id} order={order} />
                    ))
                )}
            </div>

            <AddMaintenanceDialog
                open={isAddDialogOpen}
                onOpenChange={setIsAddDialogOpen}
            />
        </div>
    );
}

function MaintenanceCard({ order }: { order: WorkOrder }) {
    const getStatusColor = (status: string) => {
        switch (status) {
            case 'Completed': return 'bg-green-100 text-green-700';
            case 'In Progress': return 'bg-blue-100 text-blue-700';
            case 'Scheduled': return 'bg-purple-100 text-purple-700';
            case 'Open': return 'bg-yellow-100 text-yellow-800';
            default: return 'bg-gray-100 text-gray-700';
        }
    };

    const getPriorityColor = (priority: string) => {
        switch (priority) {
            case 'Emergency': return 'text-red-600 bg-red-50 ring-red-600/20';
            case 'High': return 'text-orange-600 bg-orange-50 ring-orange-600/20';
            default: return 'text-gray-600 bg-gray-50 ring-gray-600/20';
        }
    };

    return (
        <div className="group relative flex flex-col gap-4 rounded-xl border border-gray-200 bg-white p-4 shadow-sm transition-all hover:shadow-md sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-start gap-4">
                <div className={cn("flex h-10 w-10 items-center justify-center rounded-full", getStatusColor(order.status))}>
                    <Wrench className="h-5 w-5" />
                </div>
                <div>
                    <div className="flex items-center gap-2">
                        <span className="font-semibold text-gray-900">{order.title}</span>
                        <span className={cn("inline-flex items-center rounded-md px-2 py-1 text-xs font-medium ring-1 ring-inset", getPriorityColor(order.priority))}>
                            {order.priority}
                        </span>
                    </div>
                    <p className="text-sm text-gray-500 line-clamp-1">{order.description}</p>
                    <div className="mt-1 flex items-center gap-4 text-xs text-gray-500">
                        <span className="flex items-center gap-1">
                            <Calendar className="h-3 w-3" />
                            {format(new Date(order.createdAt), 'MMM d, yyyy')}
                        </span>
                        <span>•</span>
                        <span>{order.category}</span>
                    </div>
                </div>
            </div>

            <div className="flex items-center gap-3 border-t pt-4 sm:border-t-0 sm:pt-0">
                <div className={cn("rounded-full px-2.5 py-0.5 text-xs font-medium", getStatusColor(order.status))}>
                    {order.status}
                </div>
                <Button variant="ghost" size="icon" className="text-gray-400">
                    <MoreVertical className="h-4 w-4" />
                </Button>
            </div>
        </div>
    );
}
